import React from "react";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";

type QuestionType =
  | "MULTIPLE_CHOICE"
  | "MULTIPLE_CHOICE_4"
  | "TRUE_FALSE"
  | "TEXT_INPUT"
  | "ESSAY"
  | "EMAIL"
  | "LETTER"
  | "AUDIO_MULTIPLE_CHOICE"
  | "AUDIO_TRUE_FALSE"
  | "MATCHING"
  | "DRAG_DROP"
  | "FILL_BLANKS"
  | "SENTENCE_ORDER";

type Question = {
  id: number;
  questionNumber: number;
  questionType: QuestionType;
  questionText: string;
  contextText?: string;
  options?: string[] | string;
  correctAnswer?: string;
  points: number;
  audioUrl?: string | null;
  audioDownloadUrl?: string | null;
  imageUrl?: string | null;
  imageDownloadUrl?: string | null;
  effectiveAudioDownloadUrl?: string | null;
  module?: string;
  usesSharedContext?: boolean;
  hasQuestionAudio?: boolean;
  hasQuestionImage?: boolean;
  matchingGroupId?: string;
};

interface MatchingQuestionsGroupProps {
  questions: Question[];
  answers: Map<number, string>;
  onAnswerChange: (questionId: number, answer: string) => void;
  disabled?: boolean;
  showCorrectAnswer?: boolean;
}

/**
 * MatchingQuestionsGroup Component
 * Displays a group of MATCHING questions together:
 * - Question type badge and text shown ONCE
 * - Image shown ONCE
 * - All dropdowns shown together
 */
const MatchingQuestionsGroup: React.FC<MatchingQuestionsGroupProps> = ({
  questions,
  answers,
  onAnswerChange,
  disabled = false,
  showCorrectAnswer = false,
}) => {
  if (questions.length === 0) return null;

  // Get shared data from first question (all have same text/image)
  const firstQuestion = questions[0];
  const imageUrl = firstQuestion.imageDownloadUrl || firstQuestion.imageUrl;
  const questionText = firstQuestion.questionText;
  const totalPoints = questions.reduce((sum, q) => sum + q.points, 0);

  // Available letters for dropdowns
  const availableLetters = [
    "a",
    "b",
    "c",
    "d",
    "e",
    "f",
    "g",
    "h",
    "i",
    "j",
    "k",
    "l",
  ];

  return (
    <div className="bg-white rounded-lg sm:rounded-xl shadow-md border border-gray-200 overflow-hidden">
      {/* Header - Shown ONCE */}
      <div className="bg-gradient-to-r from-gray-50 to-blue-50 px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-200">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-[#009fe3] to-[#0088cc] text-white rounded-lg flex items-center justify-center font-bold text-base sm:text-lg shrink-0">
              {questions[0].questionNumber}-
              {questions[questions.length - 1].questionNumber}
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
              <span className="text-lg sm:text-xl shrink-0">↔️</span>
              <span className="text-xs sm:text-sm text-gray-600 font-medium truncate">
                Zuordnung ({questions.length} Fragen)
              </span>
            </div>
          </div>
          <div className="inline-flex items-center px-2 sm:px-3 py-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-bold text-xs sm:text-sm shrink-0">
            {totalPoints}P
          </div>
        </div>
      </div>

      <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
        {/* Question-Specific Context - Shown ONCE */}
        {!firstQuestion.usesSharedContext && firstQuestion.contextText && (
          <div className="p-3 sm:p-4 bg-amber-50 border border-amber-300 rounded-lg">
            <div className="flex items-start gap-2">
              <div className="text-amber-600 text-lg sm:text-xl">📄</div>
              <div className="flex-1">
                <p className="text-xs sm:text-sm text-amber-900 leading-relaxed whitespace-pre-wrap">
                  {firstQuestion.contextText}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Image - Shown ONCE */}
        {imageUrl && (
          <div className="p-2 sm:p-3 bg-gray-50 border border-gray-300 rounded-lg">
            <img
              src={imageUrl}
              alt="Zuordnung"
              className="w-full h-auto rounded-lg shadow-sm"
              onError={(e) => {
                console.error("Failed to load image:", imageUrl);
                e.currentTarget.style.display = "none";
              }}
            />
          </div>
        )}

        {/* Question Text - Shown ONCE */}
        <div className="p-3 bg-blue-50 border-l-4 border-[#009fe3] rounded">
          <p className="text-sm sm:text-base font-semibold text-gray-900 leading-relaxed">
            {questionText}
          </p>
        </div>

        {/* All Dropdowns - Grouped Together */}
        <div className="space-y-3">
          <div className="text-xs sm:text-sm font-semibold text-gray-700 mb-2">
            Wählen Sie für jede Nummer den passenden Buchstaben:
          </div>

          {questions.map((question) => {
            const answer = answers.get(question.id) || "";
            const selectedLetter = answer ? answer.toLowerCase().trim() : "";
            const isCorrect =
              showCorrectAnswer &&
              selectedLetter === question.correctAnswer?.toLowerCase().trim();
            const isIncorrect =
              showCorrectAnswer &&
              selectedLetter &&
              selectedLetter !== question.correctAnswer?.toLowerCase().trim();

            return (
              <div
                key={question.id}
                className={`flex items-center gap-3 p-3 sm:p-4 border-2 rounded-lg transition-all ${
                  disabled ? "opacity-70" : ""
                } ${
                  isCorrect
                    ? "border-green-500 bg-green-50"
                    : isIncorrect
                    ? "border-red-500 bg-red-50"
                    : "border-gray-300 bg-white"
                }`}
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-[#009fe3] to-[#0088cc] text-white rounded-lg flex items-center justify-center font-bold text-lg sm:text-xl shrink-0">
                  {question.questionNumber}
                </div>
                <span className="text-lg sm:text-xl text-gray-600 shrink-0">
                  →
                </span>
                <select
                  value={selectedLetter}
                  onChange={(e) => onAnswerChange(question.id, e.target.value)}
                  disabled={disabled}
                  className={`flex-1 px-3 py-2 sm:px-4 sm:py-3 border-2 rounded-lg font-semibold focus:ring-2 focus:ring-[#009fe3] focus:outline-none text-sm sm:text-base ${
                    isCorrect
                      ? "border-green-500 text-green-700 bg-green-50"
                      : isIncorrect
                      ? "border-red-500 text-red-700 bg-red-50"
                      : "border-[#009fe3] text-[#009fe3] bg-white"
                  } ${disabled ? "cursor-not-allowed" : ""}`}
                >
                  <option value="">--- Wählen ---</option>
                  {availableLetters.map((letter) => (
                    <option key={letter} value={letter}>
                      {letter.toUpperCase()}
                    </option>
                  ))}
                </select>
                {isCorrect && (
                  <FaCheckCircle className="text-green-600 text-xl sm:text-2xl shrink-0" />
                )}
                {isIncorrect && (
                  <FaTimesCircle className="text-red-600 text-xl sm:text-2xl shrink-0" />
                )}
              </div>
            );
          })}
        </div>

        {/* Answer Summary */}
        {questions.some((q) => answers.get(q.id)) && (
          <div className="p-3 bg-blue-50 border-2 border-[#009fe3] rounded-lg">
            <div className="text-xs sm:text-sm font-bold text-[#009fe3] mb-2">
              📝 Ihre Zuordnung:
            </div>
            <div className="flex flex-wrap gap-2">
              {questions
                .filter((q) => answers.get(q.id))
                .map((q) => {
                  const answer = answers.get(q.id) || "";
                  return (
                    <span
                      key={q.id}
                      className="px-3 py-1 bg-white border-2 border-[#009fe3] rounded-md text-sm font-bold text-[#009fe3]"
                    >
                      {q.questionNumber} → {answer.toUpperCase()}
                    </span>
                  );
                })}
            </div>
          </div>
        )}

        {/* Show Correct Answers (if applicable) */}
        {showCorrectAnswer && (
          <div className="p-3 bg-green-50 border-2 border-green-500 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <FaCheckCircle className="text-green-600 text-lg sm:text-xl" />
              <span className="text-xs sm:text-sm font-bold text-green-800">
                Richtige Antworten:
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {questions.map((q) => (
                <span
                  key={q.id}
                  className="px-3 py-1 bg-white border-2 border-green-500 rounded-md text-sm font-bold text-green-700"
                >
                  {q.questionNumber} → {q.correctAnswer?.toUpperCase()}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MatchingQuestionsGroup;
